import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Download, Edit } from '@mui/icons-material';
import { taxBooksService } from '../../../services/api';
import EconomicActivitySelect from '../../common/EconomicActivitySelect';

const DOCUMENT_TYPE_OPTIONS = [
  { code: '01', name: 'Factura' },
  { code: '03', name: 'Comprobante de Crédito Fiscal' },
  { code: '05', name: 'Nota de Crédito' },
  { code: '06', name: 'Nota de Débito' },
  { code: '07', name: 'Comprobante de Retención' },
  { code: '09', name: 'Documento Contable de Liquidación' },
  { code: '11', name: 'Factura de Exportación' },
  { code: '14', name: 'Factura de Sujeto Excluido' },
];

const OPERATION_TYPE_OPTIONS = [
  { code: '1', name: 'Operación normal' },
  { code: '2', name: 'Rectificación o ajuste' },
  { code: '8', name: 'Operación informada en más de un anexo' },
];

const RENTA_CLASSIFICATION_OPTIONS = [
  { code: '01', name: 'Costo' },
  { code: '02', name: 'Gasto' },
  { code: '03', name: 'Activo' },
];

const COST_EXPENSE_TYPE_OPTIONS = [
  { code: '01', name: 'Compra de bienes' },
  { code: '02', name: 'Servicios' },
  { code: '03', name: 'Arrendamientos' },
  { code: '04', name: 'Otros costos o gastos' },
];

const formatNit = (value = '') => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 4) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  if (digits.length <= 13) return `${digits.slice(0, 4)}-${digits.slice(4, 10)}-${digits.slice(10)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 10)}-${digits.slice(10, 13)}-${digits.slice(13)}`;
};

const formatDui = (value = '') => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 8) return digits;
  return `${digits.slice(0, 8)}-${digits.slice(8)}`;
};

const sanitizeAlphaNum = (value = '', maxLength = 100) =>
  String(value || '').replace(/[^0-9A-Za-z-]/g, '').slice(0, maxLength);

const defaultForm = (bookType) => ({
  book_type: bookType,
  status: 'posted',
  document_class: 'E',
  document_type: bookType === 'compras_consumidor_final' ? '01' : '03',
  fecha_emision: new Date().toISOString().slice(0, 10),
  fecha_registro: new Date().toISOString().slice(0, 10),
  numero_documento: '',
  nombre_proveedor: '',
  nit_proveedor: '',
  dui_proveedor: '',
  compras_internas_exentas: 0,
  internaciones_exentas_no_sujetas: 0,
  importaciones_exentas_no_sujetas: 0,
  compras_internas_gravadas: 0,
  internaciones_gravadas_bienes: 0,
  importaciones_gravadas_bienes: 0,
  importaciones_gravadas_servicios: 0,
  credito_fiscal: 0,
  retencion_iva_13: 0,
  monto_sujeto: 0,
  total_compras: 0,
  tipo_operacion: '',
  clasificacion: '',
  sector: '',
  tipo_costo_gasto: '',
  notes: '',
});

const money = (value) => `$${(parseFloat(value || 0) || 0).toFixed(2)}`;

const PurchaseBooksPage = ({ title, subtitle, bookType }) => {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm(bookType));

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const preview = await taxBooksService.getPreview(
        bookType === 'compras_contribuyentes'
          ? 'books/purchases-taxpayers/preview'
          : 'books/purchases-consumer-final/preview',
        filters
      );
      setRows(preview.rows || []);
      setSummary(preview.summary || {});
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudieron cargar las compras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchRows = await taxBooksService.getCatalog('branches');
        setBranches(branchRows || []);
      } catch {
        setBranches([]);
      }
    };

    loadBranches();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm(bookType));
    setDialogOpen(true);
  };

  const openEdit = async (rowId) => {
    try {
      const data = await taxBooksService.getPurchase(rowId);
      setEditingId(rowId);
      setForm({
        ...defaultForm(bookType),
        ...data,
      });
      setDialogOpen(true);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo cargar la compra');
    }
  };

  const savePurchase = async () => {
    try {
      const payload = {
        ...form,
        book_type: bookType,
      };

      if (editingId) {
        await taxBooksService.updatePurchase(editingId, payload);
      } else {
        await taxBooksService.createPurchase(payload);
      }

      setDialogOpen(false);
      setEditingId(null);
      setForm(defaultForm(bookType));
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo guardar la compra');
    }
  };

  const deletePurchase = async (rowId) => {
    if (!window.confirm('¿Eliminar esta compra del libro?')) return;
    try {
      await taxBooksService.deletePurchase(rowId);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo eliminar la compra');
    }
  };

  const exportAnnex3 = async () => {
    try {
      const blob = await taxBooksService.exportCsv(
        bookType === 'compras_contribuyentes'
          ? 'books/purchases-taxpayers/export'
          : 'books/purchases-consumer-final/export',
        filters
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const month = String(filters.month).padStart(2, '0');
      link.download =
        bookType === 'compras_contribuyentes'
          ? `anexo_3_compras_contribuyentes_${filters.year}_${month}.csv`
          : `compras_consumidor_final_${filters.year}_${month}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo exportar el libro');
    }
  };

  const getDocumentTypeLabel = (code) => {
    const match = DOCUMENT_TYPE_OPTIONS.find((item) => item.code === code);
    return match ? `${match.code} - ${match.name}` : code || '-';
  };

  const getCostExpenseTypeLabel = (code) => {
    const match = COST_EXPENSE_TYPE_OPTIONS.find((item) => item.code === code);
    return match ? `${match.code} - ${match.name}` : code || '-';
  };

  const getClassificationLabel = (code) => {
    const match = RENTA_CLASSIFICATION_OPTIONS.find((item) => item.code === code);
    return match ? `${match.code} - ${match.name}` : code || '-';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<Download />} onClick={exportAnnex3} disabled={loading}>
            Generar formato MH
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Registrar compra
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={filters.month}
                onChange={(event) => setFilters((prev) => ({ ...prev, month: event.target.value }))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index + 1} value={String(index + 1)}>
                    {String(index + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Año"
                value={filters.year}
                onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filters.branchId || ''}
                onChange={(event) => setFilters((prev) => ({ ...prev, branchId: event.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadData} disabled={loading} sx={{ height: '100%' }}>
                Generar vista previa
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Documentos</Typography>
              <Typography variant="h5">{summary.count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total compras</Typography>
              <Typography variant="h5">{money(summary.totalCompras)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Crédito fiscal</Typography>
              <Typography variant="h5">{money(summary.totalCreditoFiscal)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Proveedor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>NIT</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Gravadas</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Crédito</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {loading ? 'Cargando...' : 'No hay compras registradas para el período seleccionado'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.fecha_emision_documento}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getDocumentTypeLabel(row.tipo_documento)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.numero_documento}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.nombre_proveedor}</TableCell>
                    <TableCell>{row.nit_proveedor || '-'}</TableCell>
                    <TableCell align="right">{money(row.compras_internas_gravadas)}</TableCell>
                    <TableCell align="right">{money(row.credito_fiscal)}</TableCell>
                    <TableCell align="right">{money(row.total_compras)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(row.id)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deletePurchase(row.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar compra' : 'Registrar compra'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.fecha_emision}
                onChange={(event) => setForm((prev) => ({ ...prev, fecha_emision: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fecha registro"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.fecha_registro}
                onChange={(event) => setForm((prev) => ({ ...prev, fecha_registro: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={form.branch_id || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, branch_id: event.target.value }))}
              >
                <MenuItem value="">Sin sucursal</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Tipo documento"
                value={form.document_type}
                onChange={(event) => setForm((prev) => ({ ...prev, document_type: event.target.value }))}
              >
                {DOCUMENT_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número documento"
                value={form.numero_documento}
                onChange={(event) => setForm((prev) => ({ ...prev, numero_documento: sanitizeAlphaNum(event.target.value, 100) }))}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Proveedor"
                value={form.nombre_proveedor}
                onChange={(event) => setForm((prev) => ({ ...prev, nombre_proveedor: event.target.value.slice(0, 250) }))}
                inputProps={{ maxLength: 250 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="NIT proveedor"
                value={form.nit_proveedor}
                onChange={(event) => setForm((prev) => ({ ...prev, nit_proveedor: formatNit(event.target.value) }))}
                placeholder="0000-000000-000-0"
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="DUI proveedor"
                value={form.dui_proveedor}
                onChange={(event) => setForm((prev) => ({ ...prev, dui_proveedor: formatDui(event.target.value) }))}
                placeholder="00000000-0"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Serie"
                value={form.serie_documento || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, serie_documento: sanitizeAlphaNum(event.target.value, 100) }))}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Compras gravadas"
                type="number"
                value={form.compras_internas_gravadas}
                onChange={(event) => setForm((prev) => ({ ...prev, compras_internas_gravadas: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Compras exentas"
                type="number"
                value={form.compras_internas_exentas}
                onChange={(event) => setForm((prev) => ({ ...prev, compras_internas_exentas: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Crédito fiscal"
                type="number"
                value={form.credito_fiscal}
                onChange={(event) => setForm((prev) => ({ ...prev, credito_fiscal: event.target.value }))}
                disabled={bookType === 'compras_consumidor_final'}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Total compras"
                type="number"
                value={form.total_compras}
                onChange={(event) => setForm((prev) => ({ ...prev, total_compras: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Monto sujeto"
                type="number"
                value={form.monto_sujeto}
                onChange={(event) => setForm((prev) => ({ ...prev, monto_sujeto: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Retención IVA 13%"
                type="number"
                value={form.retencion_iva_13}
                onChange={(event) => setForm((prev) => ({ ...prev, retencion_iva_13: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo operación"
                value={form.tipo_operacion}
                onChange={(event) => setForm((prev) => ({ ...prev, tipo_operacion: event.target.value }))}
              >
                <MenuItem value="">Seleccione</MenuItem>
                {OPERATION_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Clasificación"
                value={form.clasificacion}
                onChange={(event) => setForm((prev) => ({ ...prev, clasificacion: event.target.value }))}
              >
                <MenuItem value="">Seleccione</MenuItem>
                {RENTA_CLASSIFICATION_OPTIONS.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <EconomicActivitySelect
                value={form.sector}
                onChange={(value) => setForm((prev) => ({ ...prev, sector: value }))}
                label="Sector"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo costo/gasto"
                value={form.tipo_costo_gasto}
                onChange={(event) => setForm((prev) => ({ ...prev, tipo_costo_gasto: event.target.value }))}
              >
                <MenuItem value="">Seleccione</MenuItem>
                {COST_EXPENSE_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                multiline
                minRows={2}
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value.slice(0, 5000) }))}
                inputProps={{ maxLength: 5000 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePurchase}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseBooksPage;
